import { RequirementParams } from "@/backend/access/requirement/types";
import { I18n } from "@/i18n/I18n";
import { useEffect, useMemo, useState } from "react";
import { computeReviewProgress } from "@/frontend/utils/reviewProgress";
import { Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ConfirmModal } from "../modals/ConfirmModal";
import { CustomToggle } from "../dropdown/CustomToggle";
import { EditRequirementModal } from "../requirements/EditRequirementModal";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { useRequirementDocumentationStore } from "@/frontend/store/RequirementDocumentationStore";
import { useShallow } from "zustand/react/shallow";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { RequirementCategoryType } from "@/backend/access/requirementEvidenceCategory/types";
import { requirementPath } from "@/frontend/utils/requirementPath";

interface ProjectRequirementCardProps {
  requirement: RequirementParams;
}

export function ProjectRequirementCard({ requirement }: ProjectRequirementCardProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const navigate = useNavigate();
  const userClass = useGlobalStore(state => state.userClass);

  const fetchCategories = useRequirementDocumentationStore(state => state.fetchCategories);
  const deleteRequirement = useRequirementsStore(state => state.deleteRequirement);

  useEffect(() => {
    void fetchCategories(requirement.id);
  }, [requirement.id, fetchCategories]);

  const handleRemove = async () => {
    await deleteRequirement(requirement.id);
    setShowDeleteModal(false);
  };

  return (
    <>
      <div
        className="requirement-item"
        role="link"
        tabIndex={0}
        onClick={() => void navigate(requirementPath(requirement))}
      >
        <div className="d-flex w-100 justify-content-between align-items-center">
          <div>
            <div className="d-flex flex-column gap-1">
              <div style={{ fontSize: '16px', fontWeight: 500, color: 'var(--neutral-950)' }}>{requirement.name}</div>
              {requirement.identifier && (
                <div style={{ fontSize: '12px', fontWeight: 400, color: 'var(--neutral-700)' }} aria-label={I18n.get('identifier')}>
                  {requirement.identifier}
                </div>
              )}
              { !requirement.applicable && <div className="fusa-badge bg-secondary">{I18n.get('notApplicable')}</div> }
              { requirement.applicable &&  <EvidenceStatusBadge requirement={requirement} /> }
            </div>
          </div>
          {userClass?.getOrgId() === requirement.propelOrgId && (
            <Dropdown drop="down" onClick={e => e.stopPropagation()}>
            <Dropdown.Toggle size="sm" as={CustomToggle('requirement')} id={`requirement-dropdown-${requirement.id}`} />
            <Dropdown.Menu>
              <Dropdown.Item as={Link} to={requirementPath(requirement)}>
                {I18n.get('view')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowEditModal(true)}>
                {I18n.get('edit')}
              </Dropdown.Item>
              <Dropdown.Item className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {I18n.get('remove')}
              </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>

      <EditRequirementModal
        requirement={requirement}
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
      />

      {showDeleteModal && (
        <ConfirmModal
          title={I18n.get('removeRequirement')}
          confirmMessage={I18n.get('areYouSureRemoveRequirement', { title: requirement.name })}
          onConfirm={() => handleRemove()}
          setShowModal={setShowDeleteModal}
        />
      )}
    </>
  );
}

function EvidenceStatusBadge({ requirement }: { requirement: RequirementParams }) {
  const categories = useRequirementDocumentationStore(useShallow(state => (state.categoriesByRequirement[requirement.id] || []).filter(c => c.type === RequirementCategoryType.EVIDENCE)));
  const submissionsByCategory = useRequirementDocumentationStore(state => state.submissionsByCategory);

  const progress = useMemo(
    () => computeReviewProgress(categories, submissionsByCategory),
    [categories, submissionsByCategory]
  );

  if (categories.length === 0) return null;

  if (progress.hasPendingReview) {
    return <div className="fusa-badge bg-warning">{I18n.get('pendingReview')}</div>;
  }

  if (progress.isAwaitingSubmission) {
    return <div className="fusa-badge bg-primary">{I18n.get('awaitingSubmission')}</div>;
  }

  if (progress.isAllReviewed) {
    return <div className="fusa-badge bg-success">{I18n.get('reviewComplete')}</div>;
  }

  return null;
}
